import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Box, Chip, CircularProgress, Alert, FormControlLabel,
  Switch, TextField, Typography, FormControl, InputLabel, Select, MenuItem,
  IconButton, Tooltip, Tabs, Tab, Menu, ListItemText,
} from '@mui/material';
import { ArrowBack, DeleteOutline, Public, AddCircleOutline } from '@mui/icons-material';
import MDButton from 'components/MDButton';
import { useServicePlanApi } from '../services/servicePlanApi';
import { categoriesApi } from 'api/categoriesApi';
import { featuresApi } from 'api/featuresApi';
import { useDuration } from 'context/DurationContext';
import { useCountry } from 'context/CountryContext';
import { Notify } from 'notiflix';
import AddOnsSelector from './AddOnsSelector';
import {
  readTierConfig, readAddOnIds, readTierCopy,
  findRootQuestion, buildPathFromAnswers, orderByFlow, activeQuestions, questionSteps,
} from './mappingUtils';

// ── Design tokens
const ACCENT = '#667eea';
const TIER_COLOR = { low: '#34d399', mid: '#fbbf24', high: '#a78bfa' };
const TIER_DIM   = { low: 'rgba(52,211,153,0.08)', mid: 'rgba(251,191,36,0.08)', high: 'rgba(167,139,250,0.08)' };
const TIER_LABEL = { low: 'Low', mid: 'Mid', high: 'High' };
// Default user-facing plan card titles per tier (used as placeholders here and
// as the fallback on the backend when no custom title is set).
const TIER_DEFAULT_TITLE = { low: 'Essential', mid: 'Advance', high: 'Expert' };
const TIERS = ['low', 'mid', 'high'];

function TierBadge({ tier }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
      color: TIER_COLOR[tier], background: `${TIER_COLOR[tier]}22`,
      padding: '3px 10px', borderRadius: 4, border: `1px solid ${TIER_COLOR[tier]}44`,
    }}>
      {TIER_LABEL[tier]}
    </span>
  );
}

const formatDurationLabel = (duration) => {
  if (!duration) return '';
  if (duration.displayLabel) return duration.displayLabel;
  if (duration.label) return duration.label;
  const value = duration.value || 0;
  const unit = duration.unit || 'month';
  const unitLabels = { day: 'Day', week: 'Week', month: 'Month', quarter: 'Quarter', biannual: '6 Months', year: 'Year', custom: 'Custom' };
  const unitLabel = unitLabels[unit] || unit;
  return `${value} ${unitLabel}${value > 1 ? 's' : ''}`;
};

// Edit mode: the walked tree path plus every question the saved combination
// keys on, in flow order. A mapping may key on only some questions (SEO keys
// on industry, budget and keywords), and the walk stops at the first one it
// has no answer for — without this those conditions wouldn't be shown.
function buildEditPath(questions, answerCombination) {
  const ids = new Set(questions.map(q => q.id));
  const walked = buildPathFromAnswers(questions, answerCombination);
  const keyed = Object.keys(answerCombination).filter(id => ids.has(id));
  return orderByFlow(questions, [...new Set([...walked, ...keyed])]);
}

/**
 * Full-width inline form for creating OR editing an answer→plan mapping.
 * When `mapping` is provided it runs in edit mode (pre-filled + update),
 * otherwise create mode.
 */
function MappingForm({ mapping, questionnaireId, categoryIds, questions, onBack, onSaved }) {
  const isEdit = Boolean(mapping);
  const { mappingsApi } = useServicePlanApi();
  const { durations } = useDuration();
  const { countries } = useCountry();

  const filteredQuestions = activeQuestions(questions.filter(q => q.questionnaireId === questionnaireId));
  // Badge numbers match the question number the user sees on the site.
  const flowSteps = questionSteps(filteredQuestions);
  const questionnaireName = filteredQuestions[0]?.questionnaire?.name
    || `Questionnaire ${String(questionnaireId || '').slice(-8)}`;

  // ── Question tree state
  const [questionPath, setQuestionPath] = useState([]);
  const [answers,      setAnswers]      = useState({});

  // ── Feature state
  const [features,        setFeatures]        = useState([]);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [tierFeatures,    setTierFeatures]    = useState({ low: [], mid: [], high: [] });

  // ── Per-tier display copy (title / subtitle / description) for the user cards
  const emptyTierCopy = () => ({
    low:  { title: '', subtitle: '', description: '' },
    mid:  { title: '', subtitle: '', description: '' },
    high: { title: '', subtitle: '', description: '' },
  });
  const [tierCopy, setTierCopy] = useState(emptyTierCopy);

  // ── Which tier is highlighted as "Recommended" on the user cards (default mid)
  const [recommendedTier, setRecommendedTier] = useState('mid');

  // ── Pricing state: grouped by country → durations → tier prices
  //    pricing = [{ key, countryId, durations: [{ dkey, durationId, prices: { low, mid, high } }] }]
  const [pricing,       setPricing]       = useState([]);
  const [activeCountry, setActiveCountry] = useState(0);
  const idRef           = useRef(0);
  const seededRef       = useRef(false);
  const nextId          = () => (++idRef.current);
  const emptyTierPrices = () => ({ low: {}, mid: {}, high: {} });

  // ── Add-ons state
  const [addOnIds, setAddOnIds] = useState([]);

  const [active,  setActive]  = useState(true);
  const [loading, setLoading] = useState(false);

  // ── Init (mount) — pre-fill in edit mode, blank in create mode
  useEffect(() => {
    if (filteredQuestions.length === 0) return;

    if (isEdit) {
      const existingAnswers = mapping.answerCombination || {};
      const path = buildEditPath(filteredQuestions, existingAnswers);
      setQuestionPath(path.length > 0 ? path : [findRootQuestion(filteredQuestions)?.id].filter(Boolean));
      setAnswers(existingAnswers);

      const { featuresByTier, tierPricing: savedPricing } = readTierConfig(mapping);
      setTierFeatures({
        low:  Array.isArray(featuresByTier.low)  ? featuresByTier.low  : [],
        mid:  Array.isArray(featuresByTier.mid)  ? featuresByTier.mid  : [],
        high: Array.isArray(featuresByTier.high) ? featuresByTier.high : [],
      });
      setTierCopy(readTierCopy(mapping));
      const savedRec = mapping.recommendedTier || mapping.meta?.recommendedTier;
      setRecommendedTier(TIERS.includes(savedRec) ? savedRec : 'mid');

      // Rebuild pricing grouped by country (each country → its priced durations)
      const byCountry = {};
      for (const [durationId, countriesObj] of Object.entries(savedPricing || {})) {
        for (const [countryId, byTier] of Object.entries(countriesObj || {})) {
          (byCountry[countryId] = byCountry[countryId] || []).push({
            dkey: nextId(),
            durationId,
            prices: { low: byTier?.low || {}, mid: byTier?.mid || {}, high: byTier?.high || {} },
          });
        }
      }
      const built = Object.entries(byCountry).map(([countryId, durs]) => ({ key: nextId(), countryId, durations: durs }));
      setPricing(built);
      setActiveCountry(0);
      if (built.length > 0) seededRef.current = true;

      setAddOnIds(readAddOnIds(mapping));
      setActive(mapping.active !== undefined ? mapping.active : true);
    } else {
      const root = findRootQuestion(filteredQuestions);
      setQuestionPath(root ? [root.id] : []);
      setAnswers({});
      setTierFeatures({ low: [], mid: [], high: [] });
      setTierCopy(emptyTierCopy());
      setRecommendedTier('mid');
      setPricing([]);
      setActiveCountry(0);
      setAddOnIds([]);
      setActive(true);
    }
    fetchFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Seed one default country (with one default duration) once data is loaded
  //    (only if none exist yet and we haven't seeded/edited already).
  useEffect(() => {
    if (durations.length && countries.length && pricing.length === 0 && !seededRef.current) {
      seededRef.current = true;
      const d = durations.find(x => x.isDefault) || durations[0];
      const c = countries.find(x => x.isDefault) || countries[0];
      setPricing([{ key: nextId(), countryId: c.id, durations: [{ dkey: nextId(), durationId: d.id, prices: emptyTierPrices() }] }]);
      setActiveCountry(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durations, countries, pricing.length]);

  const fetchFeatures = async () => {
    setLoadingFeatures(true);
    try {
      const cats = await categoriesApi.fetchAllActiveCategories();
      // Restrict to the categories chosen on the questionnaire. When none are
      // configured, fall back to all categories (backward compatible).
      const allowed = Array.isArray(categoryIds) ? categoryIds.map(String) : [];
      const scopedCats = allowed.length
        ? (cats || []).filter(c => allowed.includes(String(c.id)))
        : (cats || []);
      const lists = await Promise.all(
        scopedCats.map(c =>
          featuresApi.getFeaturesByCategory(c.id)
            .then(fs => (fs || []).map(f => ({ ...f, categoryId: f.categoryId ?? c.id, categoryName: c.name })))
            .catch(() => [])
        )
      );
      setFeatures(lists.flat());
    } catch {
      Notify.failure('Failed to load features');
    } finally {
      setLoadingFeatures(false);
    }
  };

  // ── Answer selection (single/dropdown) — follows tree
  const handleSingleAnswer = useCallback((questionId, answerId) => {
    const question = filteredQuestions.find(q => q.id === questionId);
    const answerOpt = question?.answerOptions?.find(a => a.id === answerId);
    const nextQId = answerOpt?.nextQuestionId;

    const currentIdx = questionPath.indexOf(questionId);
    // Same follow-up question as before → the rest of the path (and its
    // answers) is still valid, e.g. picking a sub-industry on an "Any
    // answer" row must not wipe the budget/keyword conditions after it.
    if (nextQId && nextQId === questionPath[currentIdx + 1]) {
      setAnswers(prev => ({ ...prev, [questionId]: [answerId] }));
      return;
    }
    const newPath = questionPath.slice(0, currentIdx + 1);
    if (nextQId && filteredQuestions.find(q => q.id === nextQId)) newPath.push(nextQId);

    const toRemove = questionPath.slice(currentIdx + 1);
    setAnswers(prev => {
      const next = { ...prev, [questionId]: [answerId] };
      toRemove.forEach(qid => delete next[qid]);
      return next;
    });
    setQuestionPath(newPath);
  }, [questionPath, filteredQuestions]);

  // ── Answer selection (checkbox/multiple)
  const handleMultiAnswer = useCallback((questionId, answerId, checked) => {
    setAnswers(prev => {
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      const updated = checked ? [...current, answerId] : current.filter(id => id !== answerId);
      const next = { ...prev, [questionId]: updated };

      if (updated.length > 0) {
        const question = filteredQuestions.find(q => q.id === questionId);
        const firstOpt = question?.answerOptions?.find(a => a.id === updated[0]);
        const nextQId  = firstOpt?.nextQuestionId;
        const currentIdx = questionPath.indexOf(questionId);
        if (!(nextQId && nextQId === questionPath[currentIdx + 1])) {
          const newPath = questionPath.slice(0, currentIdx + 1);
          if (nextQId && filteredQuestions.find(q => q.id === nextQId)) newPath.push(nextQId);
          setQuestionPath(newPath);
        }
      }
      return next;
    });
  }, [questionPath, filteredQuestions]);

  // ── Feature toggle within a tier
  const toggleTierFeature = (tier, featureId) => {
    setTierFeatures(prev => {
      const current = prev[tier];
      const updated = current.includes(featureId)
        ? current.filter(id => id !== featureId)
        : [...current, featureId];
      return { ...prev, [tier]: updated };
    });
  };

  const setTierCopyField = (tier, field, value) =>
    setTierCopy(prev => ({ ...prev, [tier]: { ...prev[tier], [field]: value } }));

  const toggleAddOn = (id) =>
    setAddOnIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const tierTotal = (tier) =>
    tierFeatures[tier]
      .map(id => Number(features.find(f => f.id === id)?.featureDefaultPrice) || 0)
      .reduce((a, b) => a + b, 0);

  // ── Pricing block helpers
  const currencyOf = (countryId) => {
    const c = countries.find(x => x.id === countryId);
    return c?.currencySymbol || c?.currencyCode || '$';
  };
  const primaryCountry = countries.find(c => c.isDefault) || countries[0];
  const featureCurrencySym = primaryCountry?.currencySymbol || primaryCountry?.currencyCode || '$';

  const countryLabel = (countryId) => {
    const c = countries.find(x => x.id === countryId);
    return c ? `${c.flagEmoji || '🏳️'} ${c.countryName || c.name || ''}` : 'Select country';
  };
  const usedCountryIds = pricing.map(p => p.countryId);
  const allCountriesUsed = countries.length > 0 && usedCountryIds.length >= countries.length;

  // ── "Add Country" picker menu — lets the admin choose which country to add,
  // instead of silently picking the first unused one.
  const [addCountryAnchor, setAddCountryAnchor] = useState(null);
  const openAddCountryMenu = (e) => setAddCountryAnchor(e.currentTarget);
  const closeAddCountryMenu = () => setAddCountryAnchor(null);
  const availableCountries = countries.filter(x => !usedCountryIds.includes(x.id));

  const addCountry = (countryId) => {
    const c = countries.find(x => x.id === countryId);
    const d = durations.find(x => x.isDefault) || durations[0];
    if (!c) return;
    setPricing(prev => {
      const next = [...prev, { key: nextId(), countryId: c.id, durations: [{ dkey: nextId(), durationId: d?.id || '', prices: emptyTierPrices() }] }];
      setActiveCountry(next.length - 1);
      return next;
    });
    closeAddCountryMenu();
  };
  const removeCountry = (key) => {
    setPricing(prev => {
      const next = prev.filter(p => p.key !== key);
      setActiveCountry(i => Math.max(0, Math.min(i, next.length - 1)));
      return next;
    });
  };
  const setCountryId = (key, countryId) =>
    setPricing(prev => prev.map(p => p.key === key ? { ...p, countryId } : p));

  const addDuration = (key) => {
    setPricing(prev => prev.map(p => {
      if (p.key !== key) return p;
      const usedDur = p.durations.map(d => d.durationId);
      const d = durations.find(x => !usedDur.includes(x.id)) || durations[0];
      return { ...p, durations: [...p.durations, { dkey: nextId(), durationId: d?.id || '', prices: emptyTierPrices() }] };
    }));
  };
  const removeDuration = (key, dkey) =>
    setPricing(prev => prev.map(p => p.key === key ? { ...p, durations: p.durations.filter(d => d.dkey !== dkey) } : p));
  const setDurationId = (key, dkey, durationId) =>
    setPricing(prev => prev.map(p => p.key === key ? { ...p, durations: p.durations.map(d => d.dkey === dkey ? { ...d, durationId } : d) } : p));
  const setPrice = (key, dkey, tier, field, value) =>
    setPricing(prev => prev.map(p => p.key === key
      ? { ...p, durations: p.durations.map(d => d.dkey === dkey
          ? { ...d, prices: { ...d.prices, [tier]: { ...d.prices[tier], [field]: value } } }
          : d) }
      : p));

  const activeIdx   = pricing.length ? Math.min(activeCountry, pricing.length - 1) : 0;
  const activeBlock = pricing[activeIdx];

  // ── Submit
  const handleSubmit = async () => {
    const answerCombination = {};
    for (const [questionId, ansIds] of Object.entries(answers)) {
      if (Array.isArray(ansIds) && ansIds.length > 0) answerCombination[questionId] = ansIds;
    }

    if (Object.keys(answerCombination).length === 0) { Notify.failure('Please select at least one answer'); return; }

    const totalFeatures = tierFeatures.low.length + tierFeatures.mid.length + tierFeatures.high.length;
    if (totalFeatures === 0) { Notify.failure('Please assign at least one feature to a tier'); return; }

    // Collapse pricing back into { [durationId]: { [countryId]: { low, mid, high } } }
    const tierPricing = {};
    for (const country of pricing) {
      if (!country.countryId) continue;
      for (const dur of country.durations) {
        if (!dur.durationId) continue;
        tierPricing[dur.durationId] = tierPricing[dur.durationId] || {};
        tierPricing[dur.durationId][country.countryId] = { low: dur.prices.low, mid: dur.prices.mid, high: dur.prices.high };
      }
    }

    // Trim the per-tier copy so blank fields are stored as empty strings (the
    // backend treats empty as "use the default tier name / no subtitle/desc").
    const tierCopyToSave = TIERS.reduce((acc, tier) => {
      acc[tier] = {
        title:       (tierCopy[tier].title || '').trim(),
        subtitle:    (tierCopy[tier].subtitle || '').trim(),
        description: (tierCopy[tier].description || '').trim(),
      };
      return acc;
    }, {});

    const payload = {
      questionnaireId,
      answerCombination,
      featuresByTier: tierFeatures,
      tierPricing,
      tierCopy: tierCopyToSave,
      recommendedTier,
      addOnIds,
      active,
    };

    setLoading(true);
    try {
      if (isEdit) {
        await mappingsApi.update(mapping.id, payload);
        Notify.success('Mapping updated successfully');
      } else {
        await mappingsApi.create(payload);
        Notify.success('Mapping created successfully');
      }
      onSaved();
    } catch (e) {
      Notify.failure(e?.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} mapping`);
    } finally {
      setLoading(false);
    }
  };

  // ── Guards
  if (!questionnaireId) {
    return (
      <Card sx={{ p: 3, borderRadius: 3 }}>
        <BackHeader title="Mapping" subtitle="" onBack={onBack} />
        <Alert severity="warning" sx={{ mt: 2 }}>Please select a questionnaire first.</Alert>
      </Card>
    );
  }

  if (filteredQuestions.length === 0) {
    return (
      <Card sx={{ p: 3, borderRadius: 3 }}>
        <BackHeader title={isEdit ? 'Edit Mapping' : 'Create Mapping'} subtitle={questionnaireName} onBack={onBack} />
        <Alert severity="warning" sx={{ mt: 2 }}>No questions found for this questionnaire. Add questions first.</Alert>
      </Card>
    );
  }

  // Feature rows ordered by category then name
  const orderedFeatures = [...features].sort((a, b) =>
    (a.categoryName || '').localeCompare(b.categoryName || '') || (a.name || '').localeCompare(b.name || '')
  );

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
      <BackHeader
        title={isEdit ? 'Edit Mapping' : 'Create Mapping'}
        subtitle={questionnaireName}
        onBack={onBack}
      />

      <Box sx={{ px: 4, py: 3 }}>
        {/* ═══ STEP 1: Question Tree ═══ */}
        <Box mb={3}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6b7280', mb: 1.5 }}>
            Step 1 — {isEdit ? 'Answer Path' : 'Select Answer Path'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#9ca3af', mb: 2 }}>
            {isEdit
              ? 'Update the answer path. Changing an answer clears all subsequent answers.'
              : 'Answer each question in order. The next question appears based on your answer.'}
          </Typography>

          {questionPath.map((qid, pathIdx) => {
            const question = filteredQuestions.find(q => q.id === qid);
            if (!question) return null;
            const isMultiple = question.type === 'multiple';
            const currentAnswers = answers[question.id] || [];
            const hasAnswered = currentAnswers.length > 0;
            // Unanswered but followed by answered questions: this mapping
            // doesn't key on it, so any answer to it matches.
            const isAnyAnswer = !hasAnswered && questionPath.slice(pathIdx + 1).some(id => (answers[id] || []).length > 0);

            return (
              <Box key={qid} sx={{ mb: 1.5, border: '1.5px solid', borderColor: hasAnswered ? 'rgba(102,126,234,0.4)' : '#e5e7eb', borderRadius: 2.5, overflow: 'hidden', bgcolor: hasAnswered ? 'rgba(102,126,234,0.03)' : '#fff' }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: hasAnswered ? ACCENT : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{flowSteps[qid] || pathIdx + 1}</Typography>
                  </Box>
                  <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#111827' }}>{question.text}</Typography>
                  <Chip label={isMultiple ? 'Multiple' : 'Single'} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: isMultiple ? 'rgba(52,211,153,0.1)' : 'rgba(59,130,246,0.1)', color: isMultiple ? '#059669' : '#3b82f6' }} />
                  {isAnyAnswer && (
                    <Chip label="Any answer" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: 'rgba(107,114,128,0.12)', color: '#4b5563' }} />
                  )}
                  {hasAnswered && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {currentAnswers.map(aid => {
                        const opt = question.answerOptions?.find(o => o.id === aid);
                        return <Chip key={aid} label={opt?.answerText || aid} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: 'rgba(102,126,234,0.12)', color: ACCENT }} />;
                      })}
                    </Box>
                  )}
                </Box>

                <Box sx={{ px: 2, py: 1.5 }}>
                  {isAnyAnswer && (
                    <Typography sx={{ fontSize: 12, color: '#6b7280', mb: 1 }}>
                      Not used for matching — this plan is offered whatever the user answers here. Select an option only if this plan should be limited to it.
                    </Typography>
                  )}
                  {!question.answerOptions?.length ? (
                    <Alert severity="warning" sx={{ py: 0.5 }}>No answer options for this question.</Alert>
                  ) : (
                    <Box>
                      {question.answerOptions.map(opt => {
                        const isSelected = isMultiple ? currentAnswers.includes(opt.id) : currentAnswers[0] === opt.id;
                        return (
                          <Box key={opt.id}
                            onClick={() => isMultiple ? handleMultiAnswer(question.id, opt.id, !isSelected) : handleSingleAnswer(question.id, opt.id)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1.2, p: 1.2, mb: 0.8, borderRadius: 1.5, cursor: 'pointer', border: '1px solid', borderColor: isSelected ? 'rgba(102,126,234,0.4)' : '#e5e7eb', bgcolor: isSelected ? 'rgba(102,126,234,0.06)' : '#fafafa', '&:hover': { bgcolor: 'rgba(102,126,234,0.04)' }, transition: 'all 0.15s' }}>
                            <Box sx={{ width: 18, height: 18, flexShrink: 0, borderRadius: isMultiple ? '4px' : '50%', border: `2px solid ${isSelected ? ACCENT : '#d1d5db'}`, bgcolor: isSelected ? ACCENT : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {isSelected && (isMultiple
                                ? <span style={{ color: '#fff', fontSize: 11, fontWeight: 900 }}>✓</span>
                                : <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#fff' }} />)}
                            </Box>
                            <Typography sx={{ fontSize: 13, fontWeight: isSelected ? 600 : 400, color: isSelected ? '#111827' : '#374151' }}>{opt.answerText}</Typography>
                            {opt.nextQuestionId && filteredQuestions.find(q => q.id === opt.nextQuestionId) && (
                              <Chip label="→ follow-up" size="small" sx={{ height: 18, fontSize: 10, ml: 'auto', bgcolor: 'rgba(52,211,153,0.1)', color: '#059669' }} />
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* ═══ STEP 2: Features per Tier ═══ */}
        <Box mb={3}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6b7280', mb: 1.5 }}>
            Step 2 — Assign Features per Tier
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#9ca3af', mb: 2 }}>
            Choose which features belong to each tier. Every feature is listed in all three columns.
          </Typography>

          {loadingFeatures ? (
            <Box display="flex" alignItems="center" gap={1.5} sx={{ p: 3, border: '1px solid #e5e7eb', borderRadius: 2 }}>
              <CircularProgress size={18} sx={{ color: ACCENT }} />
              <Typography sx={{ fontSize: 13, color: '#6b7280' }}>Loading features…</Typography>
            </Box>
          ) : features.length === 0 ? (
            <Alert severity="info">No features found. Add features first.</Alert>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
              {TIERS.map(tier => {
                const tc = TIER_COLOR[tier];
                const selIds = tierFeatures[tier];
                return (
                  <Box key={tier} sx={{ border: `1.5px solid ${tc}44`, borderTop: `3px solid ${tc}`, borderRadius: 2, p: 1.5, bgcolor: TIER_DIM[tier], display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <TierBadge tier={tier} />
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: tc }}>
                        {selIds.length} · {featureCurrencySym}{tierTotal(tier).toFixed(2)}
                      </Typography>
                    </Box>

                    {/* Recommended selector — only one tier can be the highlighted
                        (blue) card on the user UI. Clicking sets this tier. */}
                    {(() => {
                      const isRec = recommendedTier === tier;
                      return (
                        <Box
                          onClick={() => setRecommendedTier(tier)}
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1, cursor: 'pointer', userSelect: 'none' }}
                        >
                          <Box sx={{ width: 15, height: 15, borderRadius: '50%', flexShrink: 0, border: `2px solid ${isRec ? tc : '#d1d5db'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isRec && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: tc }} />}
                          </Box>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: isRec ? tc : '#9ca3af' }}>
                            {isRec ? '★ Recommended' : 'Mark as recommended'}
                          </Typography>
                        </Box>
                      );
                    })()}

                    {/* Card copy shown on the user-facing plan card. All optional —
                        Title falls back to the default tier name (Essential/Advance/Expert). */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.2 }}>
                      <TextField
                        fullWidth size="small" label="Title"
                        placeholder={TIER_DEFAULT_TITLE[tier]}
                        value={tierCopy[tier].title}
                        onChange={e => setTierCopyField(tier, 'title', e.target.value)}
                        InputProps={{ sx: { bgcolor: '#fff', fontSize: 12 } }}
                      />
                      <TextField
                        fullWidth size="small" label="Subtitle"
                        value={tierCopy[tier].subtitle}
                        onChange={e => setTierCopyField(tier, 'subtitle', e.target.value)}
                        InputProps={{ sx: { bgcolor: '#fff', fontSize: 12 } }}
                      />
                      <TextField
                        fullWidth size="small" label="Description" multiline minRows={2}
                        value={tierCopy[tier].description}
                        onChange={e => setTierCopyField(tier, 'description', e.target.value)}
                        InputProps={{ sx: { bgcolor: '#fff', fontSize: 12 } }}
                      />
                    </Box>

                    <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5, '&::-webkit-scrollbar': { width: 5 }, '&::-webkit-scrollbar-thumb': { bgcolor: `${tc}66`, borderRadius: 3 } }}>
                      {orderedFeatures.map(feature => {
                        const isSelected = selIds.includes(feature.id);
                        const price = Number(feature.featureDefaultPrice) || 0;
                        return (
                          <Box key={feature.id} onClick={() => toggleTierFeature(tier, feature.id)} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.7, px: 0.5, cursor: 'pointer', borderBottom: `1px solid ${tc}22`, '&:last-child': { borderBottom: 'none' }, '&:hover': { bgcolor: `${tc}10` }, borderRadius: 1, transition: 'background 0.15s' }}>
                            <Box sx={{ width: 16, height: 16, borderRadius: '4px', flexShrink: 0, border: `2px solid ${isSelected ? tc : '#d1d5db'}`, bgcolor: isSelected ? tc : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}>
                              {isSelected && <span style={{ color: '#000', fontSize: 9, fontWeight: 900 }}>✓</span>}
                            </Box>
                            <Typography sx={{ flex: 1, fontSize: 12, fontWeight: isSelected ? 600 : 400, color: isSelected ? '#111827' : '#374151', lineHeight: 1.3 }}>
                              {feature.name}
                            </Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, flexShrink: 0, color: price === 0 ? '#9ca3af' : tc }}>
                              {price === 0 ? 'Free' : `${featureCurrencySym}${price.toFixed(2)}`}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* ═══ Country & Duration Specific Pricing ═══ */}
        <Box mb={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6b7280' }}>
              Pricing {pricing.length > 0 ? `· ${pricing.length} ${pricing.length === 1 ? 'country' : 'countries'}` : ''}
            </Typography>
            <MDButton
              variant="gradient" color="info" size="small"
              startIcon={<Public sx={{ fontSize: 16 }} />}
              onClick={openAddCountryMenu}
              disabled={durations.length === 0 || countries.length === 0 || allCountriesUsed}
            >
              Add Country
            </MDButton>
            <Menu anchorEl={addCountryAnchor} open={Boolean(addCountryAnchor)} onClose={closeAddCountryMenu}>
              {availableCountries.map(c => (
                <MenuItem key={c.id} onClick={() => addCountry(c.id)}>
                  <ListItemText>
                    {c.flagEmoji || '🏳️'} {c.countryName || c.name}
                    {c.currencyCode ? ` (${c.currencyCode})` : ''}
                  </ListItemText>
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <Typography sx={{ fontSize: 12, color: '#9ca3af', mb: 2 }}>
            Pick a country, then price each duration’s Low / Mid / High tiers. Add as many countries and durations as you need.
          </Typography>

          {pricing.length === 0 ? (
            <Alert severity="info">No pricing yet. Click “Add Country” to start configuring prices.</Alert>
          ) : (
            <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2.5, overflow: 'hidden' }}>
              {/* Country tabs */}
              <Tabs
                value={activeIdx}
                onChange={(e, v) => setActiveCountry(v)}
                variant="scrollable" scrollButtons="auto"
                sx={{
                  px: 1, bgcolor: '#fafafa', borderBottom: '1px solid #e5e7eb', minHeight: 44,
                  '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontSize: 13, fontWeight: 600, color: '#374151', zIndex: 1 },
                  '& .Mui-selected': { color: '#fff !important' },
                  '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                }}
              >
                {pricing.map(p => (
                  <Tab key={p.key} label={`${countryLabel(p.countryId)} (${currencyOf(p.countryId)})`} />
                ))}
              </Tabs>

              {/* Active country panel */}
              {activeBlock && (
                <Box sx={{ p: 2 }}>
                  {/* Panel header: change country + remove */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                      <InputLabel>Country</InputLabel>
                      <Select value={activeBlock.countryId} label="Country" onChange={e => setCountryId(activeBlock.key, e.target.value)}>
                        {countries.map(c => {
                          const usedElsewhere = pricing.some(p => p.key !== activeBlock.key && p.countryId === c.id);
                          return (
                            <MenuItem key={c.id} value={c.id} disabled={usedElsewhere}>
                              <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                <span>{c.flagEmoji || '🏳️'} {c.countryName || c.name}</span>
                                <Box display="flex" gap={0.5}>
                                  {c.isDefault && <Chip label="Default" size="small" color="primary" variant="outlined" />}
                                  {c.currencyCode && <Chip label={c.currencyCode} size="small" variant="outlined" />}
                                  {usedElsewhere && <Chip label="Added" size="small" />}
                                </Box>
                              </Box>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    <Box flex={1} />
                    <Tooltip title="Remove this country">
                      <IconButton size="small" onClick={() => removeCountry(activeBlock.key)} sx={{ color: '#ef4444', border: '1px solid', borderColor: 'rgba(239,68,68,0.3)', borderRadius: 1.5 }}>
                        <DeleteOutline sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  {/* Duration sections */}
                  {activeBlock.durations.map((dur, dIdx) => {
                    const sym = currencyOf(activeBlock.countryId);
                    return (
                      <Box key={dur.dkey} sx={{ border: '1px solid #eef0f3', borderRadius: 2, p: 1.8, mb: 1.5, bgcolor: '#fcfcfd' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                          <Box sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, bgcolor: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{dIdx + 1}</Typography>
                          </Box>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Duration</InputLabel>
                            <Select value={dur.durationId} label="Duration" onChange={e => setDurationId(activeBlock.key, dur.dkey, e.target.value)}>
                              {durations.map(d => {
                                const usedHere = activeBlock.durations.some(x => x.dkey !== dur.dkey && x.durationId === d.id);
                                return (
                                  <MenuItem key={d.id} value={d.id} disabled={usedHere}>
                                    <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                      <span>{formatDurationLabel(d)}</span>
                                      <Box display="flex" gap={0.5}>
                                        {d.isDefault && <Chip label="Default" size="small" color="primary" variant="outlined" />}
                                        {usedHere && <Chip label="Added" size="small" />}
                                      </Box>
                                    </Box>
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                          <Box flex={1} />
                          <Tooltip title="Remove this duration">
                            <span>
                              <IconButton size="small" onClick={() => removeDuration(activeBlock.key, dur.dkey)} disabled={activeBlock.durations.length === 1} sx={{ color: '#9ca3af' }}>
                                <DeleteOutline sx={{ fontSize: 18 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                          {TIERS.map(tier => {
                            const cell = dur.prices[tier] || {};
                            const tc = TIER_COLOR[tier];
                            return (
                              <Box key={tier} sx={{ border: `1px solid ${tc}55`, borderTop: `3px solid ${tc}`, borderRadius: 2, p: 1.5, bgcolor: '#fff' }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#111827', mb: 1 }}>Package: {TIER_LABEL[tier]}</Typography>

                                <TextField
                                  fullWidth size="small" type="number" label="Price"
                                  value={cell.finalPrice ?? ''}
                                  onChange={e => setPrice(activeBlock.key, dur.dkey, tier, 'finalPrice', e.target.value)}
                                  InputProps={{ startAdornment: <span style={{ marginRight: 4, color: '#9ca3af', fontSize: 13 }}>{sym}</span> }}
                                  sx={{ mb: 1.2 }}
                                />
                                <TextField
                                  fullWidth size="small" type="number" label="Price Before Discount"
                                  value={cell.priceBeforeDiscount ?? ''}
                                  onChange={e => setPrice(activeBlock.key, dur.dkey, tier, 'priceBeforeDiscount', e.target.value)}
                                  InputProps={{ startAdornment: <span style={{ marginRight: 4, color: '#9ca3af', fontSize: 13 }}>{sym}</span> }}
                                  sx={{ mb: 1.2 }}
                                />
                                <FormControl fullWidth size="small" sx={{ mb: 1.2 }}>
                                  <InputLabel>Select Discount Type</InputLabel>
                                  <Select value={cell.discountType ?? ''} label="Select Discount Type" onChange={e => setPrice(activeBlock.key, dur.dkey, tier, 'discountType', e.target.value)}>
                                    <MenuItem value="">None</MenuItem>
                                    <MenuItem value="percentage">Percentage</MenuItem>
                                    <MenuItem value="flat">Flat</MenuItem>
                                  </Select>
                                </FormControl>
                                <TextField
                                  fullWidth size="small" type="number" label="Discount Value"
                                  value={cell.discountValue ?? ''}
                                  onChange={e => setPrice(activeBlock.key, dur.dkey, tier, 'discountValue', e.target.value)}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}

                  {/* Add Duration */}
                  <MDButton
                    variant="outlined" color="info" size="small"
                    startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
                    onClick={() => addDuration(activeBlock.key)}
                    disabled={activeBlock.durations.length >= durations.length}
                  >
                    Add Duration
                  </MDButton>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* ═══ Add-ons ═══ */}
        <Box mb={3}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#6b7280', mb: 1.5 }}>
            Add-ons {addOnIds.length > 0 ? `(${addOnIds.length} selected)` : ''}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#9ca3af', mb: 2 }}>
            Optionally attach add-ons that users can purchase alongside this plan.
          </Typography>
          <AddOnsSelector selectedIds={addOnIds} onToggle={toggleAddOn} categoryIds={categoryIds} />
        </Box>

        {/* ═══ Active toggle ═══ */}
        <FormControlLabel
          control={<Switch checked={active} onChange={e => setActive(e.target.checked)} />}
          label={<Typography sx={{ fontSize: 13, fontWeight: 500 }}>Active (users will see this mapping)</Typography>}
        />
      </Box>

      {/* ═══ Action bar ═══ */}
      <Box sx={{ px: 4, py: 2, borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 1, position: 'sticky', bottom: 0, bgcolor: '#fff', zIndex: 1 }}>
        <MDButton onClick={onBack} color="secondary" disabled={loading}>Cancel</MDButton>
        <MDButton onClick={handleSubmit} color={isEdit ? 'success' : 'info'} disabled={loading}>
          {loading ? <CircularProgress size={18} color="inherit" /> : (isEdit ? 'Update Mapping' : 'Create Mapping')}
        </MDButton>
      </Box>
    </Card>
  );
}

// ── Sticky-ish header with back button
function BackHeader({ title, subtitle, onBack }) {
  return (
    <Box sx={{ px: 4, py: 2, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <IconButton onClick={onBack} size="small" sx={{ border: '1px solid #e5e7eb', borderRadius: 1.5 }}>
        <ArrowBack sx={{ fontSize: 18 }} />
      </IconButton>
      <Box>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 13, color: '#6b7280' }}>Questionnaire: <strong>{subtitle}</strong></Typography>
        )}
      </Box>
    </Box>
  );
}

export default MappingForm;
